import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AddHolidayInput,
  CalendarHoliday,
  CreateCalendarInput,
  ResolvedCalendar,
  UpdateCalendarInput,
  WorkCalendarSummary,
} from '@nest-recruit/shared';
import { apiDelete, apiGet, apiPatch, apiPost, ApiError } from '../../../lib/api-client';

/**
 * ADMIN-10. One query key prefix for everything calendar-shaped, so a write
 * invalidates the list, the holidays and the resolution preview together — a
 * holiday added while the preview says "system default" is exactly the sort of
 * half-refreshed screen that makes somebody stop believing the numbers.
 */
const CALENDARS_KEY = ['calendars'] as const;
const LIST_KEY = [...CALENDARS_KEY, 'list'] as const;
/** Every holiday write shares this, so a refetch waits until the last of a quick run has landed. */
const HOLIDAY_MUTATION_KEY = [...CALENDARS_KEY, 'holiday-write'] as const;

export function holidaysKey(calendarId: string | null, year: number): readonly unknown[] {
  return [...CALENDARS_KEY, 'holidays', calendarId, year];
}

/** `isEnabled`: false for someone who may not open Calendars, so nothing is asked for and refused (review W15). */
export function useCalendars(isEnabled = true): UseQueryResult<readonly WorkCalendarSummary[], ApiError> {
  return useQuery<readonly WorkCalendarSummary[], ApiError>({
    queryKey: LIST_KEY,
    queryFn: () => apiGet<readonly WorkCalendarSummary[]>('/calendars'),
    staleTime: 60_000,
    enabled: isEnabled,
  });
}

export interface HolidayResponse {
  readonly holidays: readonly CalendarHoliday[];
  readonly years: readonly number[];
}

export function useHolidays(
  calendarId: string | null,
  year: number,
): UseQueryResult<HolidayResponse, ApiError> {
  return useQuery<HolidayResponse, ApiError>({
    queryKey: holidaysKey(calendarId, year),
    queryFn: () => apiGet<HolidayResponse>(`/calendars/${calendarId}/holidays`, { query: { year } }),
    enabled: calendarId !== null,
    // Changing the year swaps the list in place rather than dropping to a skeleton
    // (CLAUDE.md §5a rule 2).
    placeholderData: (previous) => previous,
    // A holiday added to a year not yet on screen seeds that year's list; it must not be
    // refetched over before the write lands. Every write invalidates afterwards anyway.
    staleTime: 60_000,
  });
}

export interface ResolutionTarget {
  readonly departmentId?: string | null;
  readonly userId?: string | null;
}

/**
 * "Which calendar governs this, and why." Not decoration: inheritance that cannot
 * be seen is inheritance nobody can check, and the first time somebody disputes an
 * SLA number this is the screen that answers them.
 */
export function useResolvedCalendar(
  target: ResolutionTarget,
): UseQueryResult<ResolvedCalendar | null, ApiError> {
  const hasTarget = Boolean(target.departmentId ?? target.userId);
  return useQuery<ResolvedCalendar | null, ApiError>({
    queryKey: [...CALENDARS_KEY, 'resolve', target.departmentId ?? null, target.userId ?? null],
    queryFn: () =>
      apiGet<ResolvedCalendar>('/calendars/resolve', {
        query: {
          ...(target.departmentId ? { departmentId: target.departmentId } : {}),
          ...(target.userId ? { userId: target.userId } : {}),
        },
      }),
    enabled: hasTarget,
    placeholderData: (previous) => previous,
  });
}

/**
 * Creating, saving and removing a calendar invalidate the whole prefix and **await**
 * it before settling, so the button holds its pending state until the refetch has
 * landed rather than re-rendering from the stale cache and jumping (CLAUDE.md §5a
 * rule 3). A failure refreshes too, in the background: a stale save's 409 then shows
 * the latest record under what was typed, which is kept.
 */
function useCalendarMutation<TResult, TInput>(run: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: run,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CALENDARS_KEY });
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: CALENDARS_KEY });
    },
  });
}

export function useCreateCalendar() {
  return useCalendarMutation<WorkCalendarSummary, CreateCalendarInput>((input) =>
    apiPost<WorkCalendarSummary>('/calendars', input),
  );
}

export function useUpdateCalendar() {
  return useCalendarMutation<WorkCalendarSummary, { id: string } & UpdateCalendarInput>(
    ({ id, ...input }) => apiPatch<WorkCalendarSummary>(`/calendars/${id}`, input),
  );
}

export function useRemoveCalendar() {
  return useCalendarMutation<{ id: string }, { id: string; version: number }>(({ id, version }) =>
    apiDelete<{ id: string }>(`/calendars/${id}`, { version }),
  );
}

// --- Holidays: optimistic (non-negotiable #11) ---------------------------------------------

/** A holiday on screen before the server has answered. It can't be removed until it has an id. */
const PENDING_PREFIX = 'pending:';

export function isPendingHoliday(holiday: CalendarHoliday): boolean {
  return holiday.id.startsWith(PENDING_PREFIX);
}

function yearOf(isoDate: string): number {
  return Number(isoDate.slice(0, 4));
}

function byDate(a: CalendarHoliday, b: CalendarHoliday): number {
  return a.holidayDate.localeCompare(b.holidayDate);
}

/** Where the change went: one year's list of one calendar. */
interface HolidayContext {
  readonly key: readonly unknown[];
}

async function settle(queryClient: QueryClient, calendarId: string, year: number): Promise<HolidayContext> {
  const key = holidaysKey(calendarId, year);
  // A refetch already on its way would land a list without this change and undo it on screen.
  await Promise.all([queryClient.cancelQueries({ queryKey: key }), queryClient.cancelQueries({ queryKey: LIST_KEY })]);
  return { key };
}

/** Puts the holiday in, replacing its own pending row or an older copy of itself — never another holiday. */
function withHoliday(current: HolidayResponse | undefined, holiday: CalendarHoliday): HolidayResponse {
  const year = yearOf(holiday.holidayDate);
  const replaced = new Set([holiday.id, pendingId(holiday.holidayDate)]);
  return {
    holidays: [...(current?.holidays ?? []).filter((row) => !replaced.has(row.id)), holiday].sort(byDate),
    years: [...new Set([...(current?.years ?? []), year])].sort((a, b) => a - b),
  };
}

function withoutHoliday(current: HolidayResponse | undefined, id: string): HolidayResponse | undefined {
  return current ? { ...current, holidays: current.holidays.filter((row) => row.id !== id) } : current;
}

function changeCount(queryClient: QueryClient, calendarId: string, by: number): void {
  queryClient.setQueryData<readonly WorkCalendarSummary[]>(LIST_KEY, (rows) =>
    rows?.map((row) => (row.id === calendarId ? { ...row, holidayCount: Math.max(0, row.holidayCount + by) } : row)),
  );
}

/**
 * Refetch once the last holiday write of a quick run has settled. Refetching after each one would land a
 * list without the next click's holiday and make it blink out and back (CLAUDE.md §5a rule 3). While this
 * write is settling it still counts as one in flight, hence `<= 1`.
 */
function refreshWhenQuiet(queryClient: QueryClient): void {
  if (queryClient.isMutating({ mutationKey: HOLIDAY_MUTATION_KEY }) <= 1) {
    void queryClient.invalidateQueries({ queryKey: CALENDARS_KEY });
  }
}

export type AddHolidayVariables = { readonly calendarId: string } & AddHolidayInput;

/**
 * Adds at once: the date turns purple and the list gains the row before the server answers. A refusal
 * takes back only this change — never a whole earlier copy of the list, which would also take back a
 * second click made meanwhile — and the caller says why. The server's row replaces the pending one in
 * place, so nothing moves when the refetch lands. A year not yet on screen gets its list seeded.
 */
export function useAddHoliday() {
  const queryClient = useQueryClient();
  return useMutation<CalendarHoliday, ApiError, AddHolidayVariables, HolidayContext>({
    mutationKey: HOLIDAY_MUTATION_KEY,
    mutationFn: ({ calendarId, ...input }) => apiPost<CalendarHoliday>(`/calendars/${calendarId}/holidays`, input),
    onMutate: async ({ calendarId, holidayDate, name }) => {
      const context = await settle(queryClient, calendarId, yearOf(holidayDate));
      queryClient.setQueryData<HolidayResponse>(context.key, (current) => withHoliday(current, { id: pendingId(holidayDate), holidayDate, name }));
      changeCount(queryClient, calendarId, 1);
      return context;
    },
    onError: (_error, { calendarId, holidayDate }, context) => {
      if (!context) return;
      queryClient.setQueryData<HolidayResponse>(context.key, (current) => withoutHoliday(current, pendingId(holidayDate)));
      changeCount(queryClient, calendarId, -1);
    },
    onSuccess: (holiday, _variables, context) => {
      queryClient.setQueryData<HolidayResponse>(context.key, (current) => withHoliday(current, holiday));
    },
    onSettled: () => refreshWhenQuiet(queryClient),
  });
}

function pendingId(holidayDate: string): string {
  return `${PENDING_PREFIX}${holidayDate}`;
}

export interface RemoveHolidayVariables {
  readonly calendarId: string;
  readonly holiday: CalendarHoliday;
}

/** Removes at once; a refusal puts the holiday back where it was. */
export function useRemoveHoliday() {
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, ApiError, RemoveHolidayVariables, HolidayContext>({
    mutationKey: HOLIDAY_MUTATION_KEY,
    mutationFn: ({ calendarId, holiday }) => apiDelete<{ id: string }>(`/calendars/${calendarId}/holidays/${holiday.id}`),
    onMutate: async ({ calendarId, holiday }) => {
      const context = await settle(queryClient, calendarId, yearOf(holiday.holidayDate));
      queryClient.setQueryData<HolidayResponse>(context.key, (current) => withoutHoliday(current, holiday.id));
      changeCount(queryClient, calendarId, -1);
      return context;
    },
    onError: (_error, { calendarId, holiday }, context) => {
      if (!context) return;
      queryClient.setQueryData<HolidayResponse>(context.key, (current) => withHoliday(current, holiday));
      changeCount(queryClient, calendarId, 1);
    },
    onSettled: () => refreshWhenQuiet(queryClient),
  });
}

export function useAssignUserCalendar() {
  const queryClient = useQueryClient();
  return useMutation<
    { userId: string; workCalendarId: string | null },
    ApiError,
    { userId: string; workCalendarId: string | null; version: number }
  >({
    mutationFn: ({ userId, ...input }) =>
      apiPost<{ userId: string; workCalendarId: string | null }>(
        `/calendars/user-assignments/${userId}`,
        input,
      ),
    onSuccess: async () => {
      // The person's record changed as well as the calendar's, and USER-02 reads
      // the version this write just advanced.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: CALENDARS_KEY }),
        queryClient.invalidateQueries({ queryKey: ['users'] }),
      ]);
    },
  });
}
